import React, { useEffect, useState } from 'react';
import { defineMessages, injectIntl, IntlShape } from 'react-intl';
import {
  BaseCard,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@neteera/neteera-web-nextgen';
import dayjs, { Dayjs } from 'dayjs';

import { deepEqual } from 'src/utils/comparators';
import Connector, { PropsFromRedux } from './Connector';
import { TIMEFRAME_RANGE } from 'src/routes/IndividualDashboard/modules/dashboard/types';
import { StyledRangePicker } from './styled';
import {
  getFormatDateByContinent,
  MONTH_DAY_YEAR_FORMAT,
} from 'src/utils/timeUtils';
import { computeDisabledData, generateRandomKey } from './utils';

type Props = PropsFromRedux & {
  intl: IntlShape;
};

const TimeFrameSelector = ({ intl, changeTimeFrame, timeFrame }: Props) => {
  // TODO: This is a temporary fix and should be replaced
  // with a more reliable implementation
  const [dropdownMenuKey, setDropdownMenuKey] =
    useState<string>(generateRandomKey());
  const selected = timeFrame.range;

  const handleSelect = (value: TIMEFRAME_RANGE) => {
    changeTimeFrame({ range: value });
  };

  const options = [
    {
      label: intl.formatMessage(messages.day),
      value: TIMEFRAME_RANGE.DAY,
    },
    {
      label: intl.formatMessage(messages.day3),
      value: TIMEFRAME_RANGE.DAYS_3,
    },
    {
      label: intl.formatMessage(messages.week),
      value: TIMEFRAME_RANGE.WEEK,
    },
  ];
  const customOption = {
    label: intl.formatMessage(messages.custom),
    value: TIMEFRAME_RANGE.CUSTOM,
  };

  const handleCustomDateSelect = (dates: [Dayjs | null, Dayjs | null]) => {
    const [startDate, endDate] = dates;

    if (!startDate || !endDate) return;
    const isEndDateToday =
      endDate.format(getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT)) ===
      dayjs().format(getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT));

    changeTimeFrame({
      startTime: startDate.startOf('day').toISOString(),
      endTime: isEndDateToday
        ? dayjs().toISOString()
        : endDate.add(1, 'day').startOf('day').toISOString(),
    });
    // TODO: This is a temporary fix and should be replaced
    // with a more reliable implementation
    setDropdownMenuKey(generateRandomKey());
  };

  useEffect(() => {
    return () => {
      changeTimeFrame({ range: TIMEFRAME_RANGE.DAY });
    };
  }, [changeTimeFrame]);
  return (
    <BaseCard
      className={
        'relative flex gap-1 px-4 py-1.5 bg-neteera-teal-200 rounded-lg'
      }
    >
      {options.map(option => {
        const isCustomOption = option.value === TIMEFRAME_RANGE.CUSTOM;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            disabled={isCustomOption}
            className={`
            flex w-full p-4 text-sm font-medium rounded-md transition-all duration-200 justify-center outline-none ring-none
            focus:outline-none focus:ring-none
            ${
              !isCustomOption && selected === option.value
                ? 'bg-white text-gray-800 shadow-sm'
                : ''
            }
            ${
              !isCustomOption && selected !== option.value
                ? 'text-gray-600 hover:text-gray-800 hover:bg-neteera-teal-100'
                : ''
            }
            disabled:opacity-50 disabled:bg-neteera-teal-200 disabled:text-initial
          `}
            aria-pressed={selected === option.value}
          >
            {option.label}
          </button>
        );
      })}

      <DropdownMenu key={dropdownMenuKey}>
        <DropdownMenuTrigger
          className={`flex w-full p-4 rounded-md justify-center outline-none ring-none
              focus:outline-none focus:ring-none
              ${
                selected === customOption.value
                  ? 'bg-white text-gray-800 shadow-sm'
                  : ''
              }
              ${
                selected !== customOption.value
                  ? 'text-gray-600 hover:text-gray-800 hover:bg-neteera-teal-100'
                  : ''
              }
              `}
        >
          <button
            key={customOption.value}
            type="button"
            onClick={() => handleSelect(customOption.value)}
            className={`
              text-sm font-medium rounded-md transition-all duration-200 justify-center outline-none ring-none
              focus:outline-none focus:ring-none
              disabled:opacity-50 disabled:bg-neteera-teal-200 disabled:text-initial
            `}
            aria-pressed={selected === customOption.value}
          >
            {customOption.label}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <StyledRangePicker
            format={getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT)}
            isActive
            popupClassName="range-picker"
            onChange={handleCustomDateSelect}
            disabledDate={computeDisabledData}
          />
        </DropdownMenuContent>
      </DropdownMenu>
    </BaseCard>
  );
};

const messages = defineMessages({
  day: {
    id: 'TimeFrameSelector.index.day',
    defaultMessage: '24h',
  },
  day3: {
    id: 'TimeFrameSelector.index.day3',
    defaultMessage: '72h',
  },
  week: {
    id: 'TimeFrameSelector.index.week',
    defaultMessage: '7d',
  },
  custom: {
    id: 'TimeFrameSelector.index.custom',
    defaultMessage: 'Custom',
  },
});

export default Connector(
  injectIntl(
    React.memo(TimeFrameSelector, (oldProps, newProps) =>
      deepEqual(oldProps, newProps),
    ),
  ),
);
